import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/Button/Button.tsx"
import { InviteHelpDialogModel } from "@/components/Chat/NewProject/InviteHelpDialogModel.ts"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { FullWidthInput, INPUT_HEIGHT_PX, INPUT_PADDING_PX } from "@/components/Input/Input.tsx"
import { InputLikeTextarea } from "@/components/ResizeableTextarea/ResizeableTextarea.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"
import { formatErrorMessage } from "@/lib/displayUtils.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type { ProjectKey, TID } from "@/lib/client/generated/TypeDefinitions.ts"


type InviteHelpDialogProps =
  | {
      mode: "developer"
      dialogOpen: boolean
      setDialogOpen: (open: boolean) => void
      projectKey: ProjectKey
      taskId: TID
    }
  | {
      mode: "support"
      dialogOpen: boolean
      setDialogOpen: (open: boolean) => void
      taskId: TID
    }

export const InviteHelpDialog = observer((props: InviteHelpDialogProps) => {
  const title = props.mode === "developer" ? "Invite Developer" : "Request Support"

  return (
    <Dialog
      open={props.dialogOpen}
      onOpenChange={props.setDialogOpen}
      trigger={null}
      title={title}
    >
      <InviteHelpDialogContent {...props} />
    </Dialog>
  )
})

const InviteHelpDialogContent = observer((props: InviteHelpDialogProps) => {
  const context = useOrgContext()

  const model = useModel(() => {
    return new InviteHelpDialogModel(
      context,
      props.mode,
      props.mode === "developer" ? props.projectKey : undefined,
      props.taskId
    )
  }, [context, props.mode, props.mode === "developer" ? props.projectKey : undefined, props.taskId])

  const isDeveloperMode = model.isDeveloperMode.get()

  const handleSubmit = async () => {
    await model.submit(() => props.setDialogOpen(false))
  }

  return (
    <InviteFormContainer>
      {isDeveloperMode && (
        <EmailContainer>
          <FullWidthInput
            id="invite-email"
            placeholder="Email"
            data-testid="invite-email"
            value={model.email.get()}
            autoComplete="off"
            onChange={e => model.setEmail(e.target.value)}
            onBlur={model.markEmailTouched}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !model.submitting.get() && model.emailValid.get()) {
                void handleSubmit()
              }
            }}
          />

          {model.emailTouched.get() && !model.emailValid.get() && (
            <InputErrorMessage data-testid="invite-error">
              The email address is not valid
            </InputErrorMessage>
          )}
        </EmailContainer>
      )}

      <ProblemDescriptionContainer>
        <ProblemDescriptionInput
          placeholder={model.problemPlaceholder.get()}
          value={model.problem.get()}
          onChange={(e) => model.setProblem(e.target.value)}
          onBlur={model.markProblemTouched}
          maxHeight="50vh"
          data-testid="invite-problem"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !model.submitting.get() && (isDeveloperMode || model.problemValid.get())) {
              void handleSubmit()
            }
          }}
        />

        {!isDeveloperMode && model.problemTouched.get() && !model.problemValid.get() && (
          <InputErrorMessage data-testid="problem-error">
            Problem description is required
          </InputErrorMessage>
        )}
      </ProblemDescriptionContainer>

      {model.error.get() && (
        <FormErrorMessage>{formatErrorMessage(model.error.get(), "Unknown error")}</FormErrorMessage>
      )}

      <Horizontal alignItems="center" gap="8px">
        <Spring />
        <Button
          data-testid="invite-cancel"
          kind="alternative"
          size="large"
          onClick={() => props.setDialogOpen(false)}
        >
          Cancel
        </Button>

        <Button
          data-testid="invite-send"
          kind="primary"
          size="large"
          disabled={model.disabled.get()}
          onClick={handleSubmit}
        >
          {model.submitButtonText.get()}
        </Button>
      </Horizontal>
    </InviteFormContainer>
  )
})

const InviteFormContainer = styled(Vertical)`
  gap: 16px;
  margin-top: 16px;
  align-self: stretch;
`

const EmailContainer = styled.div`
  position: relative;
  min-width: 0;
`

const ProblemDescriptionInput = styled(InputLikeTextarea)`
  width: 100%;
  resize: none;
`

const ProblemDescriptionContainer = styled.div`
  position: relative;
`

const InputErrorMessage = styled(Horizontal)`
  align-items: center;

  position: absolute;
  right: ${INPUT_PADDING_PX}px;
  top: 0;

  height: ${INPUT_HEIGHT_PX}px;

  color: var(--error-color);

  ${textStyles.smallestFontSize};
`

const FormErrorMessage = styled.div`
  color: var(--error-color);
`
